/**
 * Dance type classification helpers (frontend_v2).
 *
 * Goal: keep filters + badges consistent and avoid false positives from long descriptions.
 * For SalsaVida specifically, infer Salsa/Bachata primarily from the event title (subject line),
 * and only fall back to description if the title is ambiguous.
 */

const WORD_RE = {
  salsa: /\bsalsa\b/i,
  bachata: /\bbachata\b/i,
};

function toLowerSafe(value) {
  return (typeof value === 'string' ? value : '').toLowerCase();
}

/**
 * Infer Salsa/Bachata tags for SalsaVida events.
 *
 * Rules:
 * - Prefer Event Name (subject line) for classification.
 * - If title contains both words, return both tags (cross-listed event).
 * - Only if title contains neither, fall back to description.
 * - If still unknown, default to Salsa (SalsaVida calendar baseline).
 */
export function inferSalsaVidaTags(event) {
  const eventName = toLowerSafe(event?.['Event Name']);
  const description = toLowerSafe(event?.Description);

  const titleHasSalsa = WORD_RE.salsa.test(eventName);
  const titleHasBachata = WORD_RE.bachata.test(eventName);

  if (titleHasSalsa || titleHasBachata) {
    const tags = new Set();
    if (titleHasSalsa) tags.add('Salsa');
    if (titleHasBachata) tags.add('Bachata');
    return tags;
  }

  const descHasSalsa = WORD_RE.salsa.test(description);
  const descHasBachata = WORD_RE.bachata.test(description);

  if (descHasSalsa || descHasBachata) {
    const tags = new Set();
    if (descHasSalsa) tags.add('Salsa');
    if (descHasBachata) tags.add('Bachata');
    return tags;
  }

  return new Set(['Salsa']);
}

export function getSalsaVidaPrimaryTag(tags) {
  if (!tags || !(tags instanceof Set)) return 'Salsa';
  if (tags.has('Bachata') && !tags.has('Salsa')) return 'Bachata';
  return 'Salsa';
}

export function tagLabelToClassSuffix(label) {
  switch (label) {
    case 'Tango':
      return 'tango';
    case 'Fusion':
      return 'fusion';
    case 'Salsa':
      return 'salsa';
    case 'Bachata':
      return 'bachata';
    case 'West Coast Swing':
      return 'wcs';
    case 'Brazilian Zouk':
      return 'zouk';
    case 'Contact Improvisation':
    case 'Improvisation':
    case 'Ecstatic Dance':
      return 'contact';
    case 'Meditation':
      return 'zouk';
    case 'Yoga':
      return 'contact';
    case 'Dance':
      return 'fusion';
    default:
      return '';
  }
}

